import { System } from './System'

import * as THREE from '../extras/three'
import { initYoga } from '../extras/yoga'

let worker

/**
 * Client System
 *
 * - Runs on the client
 *
 *
 */
export class Client extends System {
  constructor(world) {
    super(world)
    window.world = world
    window.THREE = THREE
    // quick debug helpers exposed in console (guarded)
    try {
      Object.defineProperty(window.world, 'debug', {
        get: () => ({
          octree: {
            toggle: enabled => this.world.stage.octree.toggleHelper(enabled),
            depth: () => this.world.stage.octree.getDepth(),
            count: () => this.world.stage.octree.getCount(),
          },
          springs: () => {
            try {
              const inst = this.world.entities.player?.avatar?.instance
              return inst?.debug?.listSprings?.()
            } catch (_) {}
            return []
          },
          springAxes: (size, color) => {
            try {
              const inst = this.world.entities.player?.avatar?.instance
              return inst?.debug?.showSpringAxes?.(size, color)
            } catch (_) {}
            return []
          },
          bones: {
            toggle: enabled => {
              try {
                const inst = this.world.entities.player?.avatar?.instance
                if (inst && inst.setBonesVisible) {
                  inst.setBonesVisible(enabled)
                  console.log(`🦴 Bones visibility: ${enabled ? 'ON' : 'OFF'}`)
                  return true
                }
                console.log('❌ No VRM found with bone visibility support')
                return false
              } catch (e) {
                console.error('Error toggling bone visibility:', e)
                return false
              }
            },
          },
        }),
        configurable: true,
      })
    } catch (_) {}
  }

  async init({ loadYoga }) {
    await loadYoga
    initYoga()
  }

  start() {
    this.world.graphics.renderer.setAnimationLoop(this.world.tick)
    document.addEventListener('visibilitychange', this.onVisibilityChange)

    this.world.settings.on('change', this.onSettingsChange)
  }

  onSettingsChange = changes => {
    if (changes.title) {
      document.title = changes.title.value || 'World'
    }
    if (changes.favicon) {
      const faviconUrl = changes.favicon.value?.url
        ? this.world.resolveURL(changes.favicon.value.url)
        : null
      let link = document.querySelector('link[rel="icon"]')
      if (faviconUrl) {
        if (!link) {
          link = document.createElement('link')
          link.rel = 'icon'
          document.head.appendChild(link)
        }
        link.href = faviconUrl
      } else if (link) {
        link.remove()
      }
    }
  }

  onVisibilityChange = () => {
    // if the tab is no longer active, browsers stop triggering requestAnimationFrame.
    // this is obviously bad because physics stop running and we stop processing websocket messages etc.
    // instead, we stop using requestAnimationFrame and get a worker to tick at a slower rate using setInterval
    // and notify us.
    // this allows us to keep everything running smoothly.
    // See: https://gamedev.stackexchange.com/a/200503 (kinda fucking genius)
    //
    // spawn worker if we haven't yet
    if (!worker) {
      const script = `
        const rate = 1000 / 5 // 5 FPS
        let intervalId = null;
        self.onmessage = e => {
          if (e.data === 'start' && !intervalId) {
            intervalId = setInterval(() => {
              self.postMessage(1);
            }, rate);
            console.log('[worker] tick started')
          }
          if (e.data === 'stop' && intervalId) {
            clearInterval(intervalId);
            intervalId = null;
            console.log('[worker] tick stopped')
          }
        }
      `
      const blob = new Blob([script], { type: 'application/javascript' })
      worker = new Worker(URL.createObjectURL(blob))
      worker.onmessage = () => {
        const time = performance.now()
        this.world.tick(time)
      }
    }
    if (document.hidden) {
      // stop rAF
      this.world.graphics.renderer.setAnimationLoop(null)
      // tell the worker to start
      worker.postMessage('start')
    } else {
      // tell the worker to stop
      worker.postMessage('stop')
      // resume rAF
      this.world.graphics.renderer.setAnimationLoop(this.world.tick)
    }
  }

  destroy() {
    this.world.graphics.renderer.setAnimationLoop(null)
    worker?.postMessage('stop')
    worker = null
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  }
}
